/**
 * Helpers for working with `process.env`.
 * Note: The client only has access to `NODE_ENV`, `DD_VERSION`, and `REACT_APP_**` variables on `process.env` (see `config/env.ts`)
 */

import packageJson from '../../../package.json';
import { trimTrailingSlashes } from './text';

// Merge default env variables with runtime variables
const windowEnv = (typeof window === 'undefined' ? {} : window)?._env_ ?? {};
export const env = {
  ...process.env,
  ...windowEnv,
};

const BASE_ROUTE = '/pets';

export const ENV = env?.REACT_APP_ENV;
export const VERSION = env?.DD_VERSION;
export const CONTENT_API_URL = trimTrailingSlashes(get('REACT_APP_CONTENT_API_URL')) || '';
export const CHEWY_API_URL = trimTrailingSlashes(get('REACT_APP_CHEWY_API_URL')) || '';
export const PET_PROFILE_A_URL = trimTrailingSlashes(get('REACT_APP_PET_PROFILE_A_URL')) || '';
export const BLOG_URL = trimTrailingSlashes(get('REACT_APP_BLOG_URL')) || '';
export const BLOG_ASSET_URL = trimTrailingSlashes(get('REACT_APP_BLOG_ASSET_URL')) || '';
export const PET_MD_URL = trimTrailingSlashes(get('REACT_APP_PET_MD_URL')) || '';
export const PET_MD_ASSET_URL = trimTrailingSlashes(get('REACT_APP_PET_MD_ASSET_URL')) || '';
export const CMS_URL = trimTrailingSlashes(get('REACT_APP_CMS_URL')) || '';
export const OPTIMIZELY_SDK_KEY = get('REACT_APP_OPTIMIZELY_SDK_KEY') || '';
export const HEADER_FOOTER_SPA_BASE_URL =
  trimTrailingSlashes(get('REACT_APP_HEADER_FOOTER_SPA_URL')) || '';
export const MAX_PETS = 25;
export const SERVER_TIME_ZONE = 'America/New_York';
export const SERVICE_NAME = env?.DD_SERVICE ?? packageJson.name ?? 'unknown-app';

// page types for header/footer configuration
export const PAGE_TYPE_MAIN = 'pet_profile';
export const PAGE_TYPE_MEMORIAL = 'pet_memorial';

//routes
export const getPetProfilePath = (petId: string | number = ':petId([0-9]+)') =>
  `${BASE_ROUTE}/view/${petId}`;
export const getPetsPath = () => `${BASE_ROUTE}/`;
export const getPetAllPath = () => `${BASE_ROUTE}/all`;
export const getPetNewPath = () => `${BASE_ROUTE}/new`;
export const getPetEditPath = (petId: string | number = ':petId([0-9]+)') =>
  `${BASE_ROUTE}/edit/${petId}`;
export const getPetManagePath = (petId: string | number = ':petId([0-9]+)') =>
  `${BASE_ROUTE}/manage/${petId}`;
export const getPetMemorialPath = (petId: string | number = ':petId([0-9]+)') =>
  `${BASE_ROUTE}/memorial/${petId}`;

export const componentsPath = '/pets/ux-components';
export const puppyGuide = '/pets/starter/puppy';
export const foodFinderPath = '/foodfinder';
export const mintPath = '/pets/mint';
export const formPath = '/pets/form';
export const avatarPath = '/pets/avatars';
export const contactPath = '/app/content/contact';
export const autoshipPath = '/app/subs';
export const emailUsPath = 'https://service.chewy.com/messageus';

export const ACCOUNT_OVERVIEW_PATH = '/app/account';
export const LOG_LEVEL = get('REACT_APP_LOG_LEVEL');
export const AUTOSHIP_DEACTIVATED_PETS_ENABLED = 'autoship.deactivatedPets.enabled';
export const CONFIGURED_FORM_ENABLED = 'configDrivenForm.enabled';
export const MINT_ON_VIEW_ENABLED = 'mintOnView.enabled';
export const IS_ALE_ENABLED = 'sfpa.analytics.public.pet_profile.ale_enabled';
export const IS_PORTAL_ENABLED = 'chewy_portal.enabled';
export const isProd = ENV === 'prd';
export const isLocal = ENV === 'local';

const avatarVersionedPath = 'lib/pet-profile/1.0.0/img/avatars';

export const defaultAvatarUrls = {
  dog: `${CMS_URL}/${avatarVersionedPath}/default_dog.png`,
  cat: `${CMS_URL}/${avatarVersionedPath}/default_cat.png`,
  fish: `${CMS_URL}/${avatarVersionedPath}/default_fish.png`,
  bird: `${CMS_URL}/${avatarVersionedPath}/default_bird.png`,
  small_pet: `${CMS_URL}/${avatarVersionedPath}/default_small_pet.png`,
  reptile: `${CMS_URL}/${avatarVersionedPath}/default_reptile.png`,
  horse: `${CMS_URL}/${avatarVersionedPath}/default_horse.png`,
  farm_animal: `${CMS_URL}/${avatarVersionedPath}/default_farm_animal.png`,
};

/**
 * Until we are able to use `https` URLs for SSR, we occasionally need duel env variables.
 * This functions swaps out the client value with the server value, if one exists.
 *
 * @param key Client env variable name
 */
function get(key: string) {
  if (__BROWSER__) {
    return env?.[key];
  }
  return env?.[key.replace('REACT_APP_', 'SERVER_')] ?? env?.[key];
}

if (!isProd) {
  // eslint-disable-next-line no-console
  console.log('Env Config:', {
    CONTENT_API_URL,
    CHEWY_API_URL,
    VERSION,
  });
}
